import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  buildJobLabelText,
  jobLabelTextColor,
} from './job-label.util';

@Component({
  selector: 'app-job-label',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      #labelEl
      class="pp-job-label"
      [style.backgroundColor]="colour || '#e2e8f0'"
      [style.color]="textColor"
      [pTooltip]="activeTooltip"
      tooltipPosition="top"
      [showDelay]="400"
      [tooltipDisabled]="!activeTooltip">
      {{ labelText }}
    </span>
  `,
  styles: [
    `
      :host {
        display: block;
        max-width: min(100%, 220px);
        min-width: 0;
      }
      .pp-job-label {
        display: block;
        max-width: 100%;
        box-sizing: border-box;
        padding: 0.2rem 0.55rem;
        border-radius: 999px;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.02em;
        line-height: 1.25;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: middle;
      }
    `,
  ],
})
export class JobLabelComponent implements AfterViewInit, OnChanges {
  @Input() clientName = '';
  @Input() projectName = '';
  @Input() colour = '#e2e8f0';
  @Input() title = '';

  @ViewChild('labelEl') labelEl?: ElementRef<HTMLElement>;

  /** Full text when truncated (or forced via title); empty when fully visible. */
  activeTooltip = '';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get labelText(): string {
    return buildJobLabelText(this.clientName, this.projectName);
  }

  get textColor(): string {
    return jobLabelTextColor(this.colour);
  }

  get fullTooltip(): string {
    return this.title || this.labelText;
  }

  ngAfterViewInit(): void {
    this.scheduleTruncationCheck();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.scheduleTruncationCheck();
  }

  private scheduleTruncationCheck(): void {
    requestAnimationFrame(() => this.updateTruncationTooltip());
  }

  private updateTruncationTooltip(): void {
    const el = this.labelEl?.nativeElement;
    const full = this.fullTooltip;
    if (!el || !full) {
      this.activeTooltip = '';
      this.cdr.markForCheck();
      return;
    }
    // Forced title always shows; otherwise only when text is ellipsized.
    const truncated = el.scrollWidth > el.clientWidth + 1;
    this.activeTooltip = this.title || truncated ? full : '';
    this.cdr.markForCheck();
  }
}
